import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { Check, MapPin, TriangleAlert } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { PoliticalEntitiesApi, PoliticalEntityInput } from '@/services/political/politicalEntitiesApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { PoliticalEntity } from '../types/models'
import { AuthService } from '@/services/core/auth'

interface NewPayerModalProps {
  open: boolean
  onClose: () => void
  onCreated: (entity: PoliticalEntity) => void
}

const EMPTY_VALUES = {
  name: '',
  legalEntity: '',
  description: '',
  contactEmail: '',
  vatId: '',
  ownerTeamId: '',
  parentId: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  addrState: '',
  postCode: '',
  country: '',
}

export function NewPayerModal({ open, onClose, onCreated }: NewPayerModalProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const hasToken = AuthService.isAuthenticated()
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: entities = [] } = useQuery({
    queryKey: ['politicalEntities'],
    queryFn: () => PoliticalEntitiesApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const controllers = entities.filter((e) => e.type === 'CONTROLLER')
  const [error, setError] = useState('')

  const LEGAL_ENTITY_OPTIONS = [
    { label: t('political.legalPublic'), value: 'CORPORATE_ENTITY_PUBLIC' },
    { label: t('political.legalPrivate'), value: 'CORPORATE_ENTITY_PRIVATE' },
    { label: t('political.legalParty'), value: 'POLITICAL_PARTY' },
    { label: t('political.legalIndividual'), value: 'INDIVIDUAL' },
  ]

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: ({ value }) => {
      setError('')
      mutation.mutate({
        name: value.name,
        type: 'PAYER',
        description: value.description,
        legalEntity: value.legalEntity,
        contactEmail: value.contactEmail,
        vatId: value.vatId,
        ownerTeamId: value.ownerTeamId || undefined,
        parentId: value.parentId || undefined,
        address: {
          addressLine1: value.addressLine1,
          addressLine2: value.addressLine2,
          city: value.city,
          state: value.addrState,
          postCode: value.postCode,
          country: value.country,
        },
      })
    },
  })

  const mutation = useMutation({
    mutationFn: (input: PoliticalEntityInput) => {
      const id = crypto.randomUUID().replace(/-/g, '').slice(0, 16)
      return PoliticalEntitiesApi.create(id, input)
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['politicalEntities'] })
      form.reset(EMPTY_VALUES)
      onCreated(created)
    },
    onError: (err) => {
      setError((err as Error).message)
    },
  })

  const handleClose = () => {
    form.reset(EMPTY_VALUES)
    setError('')
    onClose()
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    e.stopPropagation()
    form.handleSubmit()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent style={{ maxWidth: 560 }}>
        <DialogHeader>
          <DialogTitle>{t('newPayer.header')}</DialogTitle>
          <DialogDescription className="sr-only">{t('newPayer.header')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="p-field">
            <label>{t('newPayer.nameLabel')}</label>
            <form.Field name="name">
              {(field) => (
                <Input
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('newPayer.namePlaceholder')}
                  required
                  autoFocus
                />
              )}
            </form.Field>
          </div>

          <div className="p-field">
            <label>{t('newPayer.legalEntity')}</label>
            <form.Field name="legalEntity">
              {(field) => (
                <Select value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('newPayer.selectLegalEntity')}>{LEGAL_ENTITY_OPTIONS.find((o) => o.value === field.state.value)?.label ?? null}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                    {LEGAL_ENTITY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </form.Field>
          </div>

          <div className="p-field">
            <label>{t('common.description')}</label>
            <form.Field name="description">
              {(field) => (
                <Textarea
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('newPayer.descriptionPlaceholder')}
                  rows={2}
                  style={{ resize: 'vertical' }}
                />
              )}
            </form.Field>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div className="p-field" style={{ flex: 1 }}>
              <label>{t('newPayer.contactEmail')}</label>
              <form.Field name="contactEmail">
                {(field) => (
                  <Input
                    type="email"
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('newPayer.contactEmailPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
            <div className="p-field" style={{ flex: 1 }}>
              <label>{t('newPayer.vatId')}</label>
              <form.Field name="vatId">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('newPayer.vatIdPlaceholder')}
                  />
                )}
              </form.Field>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <div className="p-field" style={{ flex: 1 }}>
              <label>{t('newPayer.ownerTeam')}</label>
              <form.Field name="ownerTeamId">
                {(field) => (
                  <Select value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('newPayer.selectTeam')}>{teams.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                      {teams.map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            </div>
            <div className="p-field" style={{ flex: 1 }}>
              <label>{t('newPayer.parentController')}</label>
              <form.Field name="parentId">
                {(field) => (
                  <Select value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('newPayer.selectController')}>{controllers.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__clear__">{t('common.none', '— None —')}</SelectItem>
                      {controllers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </form.Field>
            </div>
          </div>

          <div style={{ borderTop: '1px solid var(--ad-border)', paddingTop: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--ad-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10, display: 'block' }}>
              <MapPin className="inline size-3" style={{ marginRight: 6 }} />
              {t('address.title')}
            </label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <form.Field name="addressLine1">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.streetPlaceholder')}
                  />
                )}
              </form.Field>
              <form.Field name="addressLine2">
                {(field) => (
                  <Input
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    placeholder={t('address.aptPlaceholder')}
                  />
                )}
              </form.Field>
              <div style={{ display: 'flex', gap: 10 }}>
                <form.Field name="city">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.cityPlaceholder')}
                      style={{ flex: 1 }}
                    />
                  )}
                </form.Field>
                <form.Field name="addrState">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.statePlaceholder')}
                      style={{ flex: 1 }}
                    />
                  )}
                </form.Field>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <form.Field name="postCode">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.postCodePlaceholder')}
                      style={{ flex: 1 }}
                    />
                  )}
                </form.Field>
                <form.Field name="country">
                  {(field) => (
                    <Input
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder={t('address.countryPlaceholder')}
                      style={{ flex: 1 }}
                    />
                  )}
                </form.Field>
              </div>
            </div>
          </div>

          {error && (
            <div style={{ color: 'var(--ad-danger)', fontSize: 13 }}>
              <TriangleAlert className="inline size-3.5" style={{ marginRight: 6 }} />
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="sm"
              loading={mutation.isPending}
              onClick={handleSubmit}
            >
              <Check className="size-4" />
              {t('newPayer.createPayer')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
